import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  PillIcon,
  UserIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  RefreshCwIcon,
  EyeIcon,
  X
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'react-hot-toast';

interface Medication {
  id: string;
  patientId: string;
  visitId?: string;
  name: string;
  genericName?: string;
  dosage: string;
  frequency: string;
  route: string;
  prescribedDate: string;
  endDate?: string;
  status: 'ACTIVE' | 'DISCONTINUED' | 'COMPLETED' | 'SUSPENDED';
  notes?: string;
  prescribedBy: string;
  refillCount: number;
  patient: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  visit?: {
    doctor: {
      user: {
        firstName: string;
        lastName: string;
      };
    };
  };
  refills?: Array<{
    id: string;
    refillDate: string;
    quantity: number;
    prescribedBy: string;
    notes?: string;
  }>;
}

interface Patient {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Doctor {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const MedicationsPage: React.FC = () => {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [patientFilter, setPatientFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(null);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [refillMedication, setRefillMedication] = useState<Medication | null>(null);

  useEffect(() => {
    fetchMedications();
    fetchPatients();
    fetchDoctors();
  }, []);

  const fetchMedications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/medications');
      setMedications(response.data.medications || []);
    } catch (error) {
      console.error('Error fetching medications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.data?.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response.data.data?.doctors || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const filteredMedications = medications.filter(medication => {
    const matchesSearch = 
      medication.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medication.genericName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medication.patient.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medication.patient.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medication.dosage.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || medication.status === statusFilter;
    const matchesPatient = !patientFilter || medication.patientId === patientFilter;
    
    return matchesSearch && matchesStatus && matchesPatient;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DISCONTINUED':
        return 'bg-red-100 text-red-800';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800';
      case 'SUSPENDED':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleRefill = async (medication: Medication) => {
    setRefillMedication(medication);
    setShowRefillModal(true);
  };

  const handleDiscontinue = async (medicationId: string) => {
    try {
      await api.post(`/medications/${medicationId}/discontinue`, {
        reason: 'Discontinued by healthcare provider'
      });
      fetchMedications(); // Refresh the list
    } catch (error) {
      console.error('Error discontinuing medication:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Medications</h1>
            <p className="text-gray-600">Manage patient medications and prescriptions</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>New Medication</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search medications, patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="DISCONTINUED">Discontinued</option>
            <option value="COMPLETED">Completed</option>
            <option value="SUSPENDED">Suspended</option>
          </select>

          {/* Patient Filter */}
          <select
            value={patientFilter}
            onChange={(e) => setPatientFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Patients</option>
            {patients.map((patient) => (
              <option key={patient.id} value={patient.id}>
                {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setPatientFilter('');
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <PillIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Medications</p>
              <p className="text-2xl font-bold text-gray-900">{medications.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {medications.filter(m => m.status === 'ACTIVE').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Discontinued</p>
              <p className="text-2xl font-bold text-gray-900">
                {medications.filter(m => m.status === 'DISCONTINUED').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangleIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-gray-900">
                {medications.filter(m => {
                  if (!m.endDate || m.status !== 'ACTIVE') return false;
                  const endDate = new Date(m.endDate);
                  const now = new Date();
                  const daysDiff = (endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
                  return daysDiff <= 30 && daysDiff > 0;
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Medications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medication
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dosage & Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prescribed Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Refills
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMedications.map((medication) => (
                <tr key={medication.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {medication.patient.user.firstName} {medication.patient.user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {medication.patient.user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{medication.name}</div>
                      {medication.genericName && (
                        <div className="text-sm text-gray-500">{medication.genericName}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900">{medication.dosage}</div>
                      <div className="text-sm text-gray-500">{medication.frequency}</div>
                      <div className="text-sm text-gray-500">{medication.route}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(medication.prescribedDate)}
                    </div>
                    {medication.endDate && (
                      <div className="text-sm text-gray-500">
                        Ends: {formatDate(medication.endDate)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(medication.status)}`}>
                      {medication.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {medication.refillCount} refills
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedMedication(medication)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {medication.status === 'ACTIVE' && (
                        <>
                          <button
                            onClick={() => handleRefill(medication)}
                            className="text-green-600 hover:text-green-900"
                            title="Refill"
                          >
                            <RefreshCwIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDiscontinue(medication.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Discontinue"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMedications.length === 0 && (
          <div className="text-center py-12">
            <PillIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No medications found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter || patientFilter 
                ? 'Try adjusting your filters.' 
                : 'Get started by prescribing a new medication.'}
            </p>
            {!searchTerm && !statusFilter && !patientFilter && (
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  New Medication
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Medication Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Prescribe New Medication</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const medicationData = {
                  patientId: formData.get('patientId') as string,
                  medicationName: formData.get('medicationName') as string,
                  genericName: formData.get('genericName') as string,
                  dosage: formData.get('dosage') as string,
                  frequency: formData.get('frequency') as string,
                  route: formData.get('route') as 'ORAL' | 'INJECTION' | 'TOPICAL' | 'INHALATION' | 'OTHER',
                  startDate: formData.get('startDate') as string,
                  endDate: formData.get('endDate') as string || undefined,
                  quantity: parseInt(formData.get('quantity') as string),
                  refills: parseInt(formData.get('refills') as string),
                  instructions: formData.get('instructions') as string,
                  notes: formData.get('notes') as string || undefined,
                  status: 'ACTIVE' as const
                };

                try {
                  await api.post('/medications', medicationData);
                  toast.success('Medication prescribed successfully');
                  setShowCreateModal(false);
                  fetchMedications();
                } catch (error) {
                  console.error('Error prescribing medication:', error);
                  toast.error('Failed to prescribe medication');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Medication Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Medication Name *
                    </label>
                    <input
                      type="text"
                      name="medicationName"
                      required
                      placeholder="e.g., Lisinopril, Metformin"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Generic Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Generic Name *
                    </label>
                    <input
                      type="text"
                      name="genericName"
                      required
                      placeholder="e.g., Lisinopril, Metformin hydrochloride"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Dosage */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dosage *
                    </label>
                    <input
                      type="text"
                      name="dosage"
                      required
                      placeholder="e.g., 10mg, 500mg"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequency *
                    </label>
                    <select
                      name="frequency"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select frequency</option>
                      <option value="ONCE_DAILY">Once daily</option>
                      <option value="TWICE_DAILY">Twice daily</option>
                      <option value="THRICE_DAILY">Three times daily</option>
                      <option value="FOUR_TIMES_DAILY">Four times daily</option>
                      <option value="AS_NEEDED">As needed</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                    </select>
                  </div>

                  {/* Route */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Route *
                    </label>
                    <select
                      name="route"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select route</option>
                      <option value="ORAL">Oral</option>
                      <option value="INJECTION">Injection</option>
                      <option value="TOPICAL">Topical</option>
                      <option value="INHALATION">Inhalation</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      required
                      min="1"
                      placeholder="e.g., 30"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Refills */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Refills *
                    </label>
                    <input
                      type="number"
                      name="refills"
                      required
                      min="0"
                      max="12"
                      placeholder="e.g., 3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Instructions */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instructions *
                  </label>
                  <textarea
                    name="instructions"
                    required
                    rows={3}
                    placeholder="e.g., Take with food, Take in the morning, Avoid alcohol..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    placeholder="Any additional notes about the medication..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Prescribe Medication
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Medication Details Modal */}
      {selectedMedication && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900">Medication Details</h3>
              <div className="mt-4 text-sm text-gray-600 space-y-2">
                <p><strong>Patient:</strong> {selectedMedication.patient.user.firstName} {selectedMedication.patient.user.lastName}</p>
                <p><strong>Medication:</strong> {selectedMedication.name}</p>
                {selectedMedication.genericName && <p><strong>Generic:</strong> {selectedMedication.genericName}</p>}
                <p><strong>Dosage:</strong> {selectedMedication.dosage}</p>
                <p><strong>Frequency:</strong> {selectedMedication.frequency}</p>
                <p><strong>Route:</strong> {selectedMedication.route}</p>
                <p><strong>Prescribed:</strong> {formatDate(selectedMedication.prescribedDate)}</p>
                {selectedMedication.endDate && <p><strong>End Date:</strong> {formatDate(selectedMedication.endDate)}</p>}
                <p><strong>Status:</strong> {selectedMedication.status}</p>
                <p><strong>Refills:</strong> {selectedMedication.refillCount}</p>
                {selectedMedication.notes && <p><strong>Notes:</strong> {selectedMedication.notes}</p>}
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => setSelectedMedication(null)}
                  className="px-4 py-2 bg-primary-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refill Modal */}
      {showRefillModal && refillMedication && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900">Create Refill</h3>
              <p className="text-sm text-gray-500 mt-2">
                Refill for {refillMedication.name}
              </p>
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Patient:</strong> {refillMedication.patient.user.firstName} {refillMedication.patient.user.lastName}</p>
                <p><strong>Current Refills:</strong> {refillMedication.refillCount}</p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => setShowRefillModal(false)}
                  className="px-4 py-2 bg-primary-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationsPage; 